import { w3cwebsocket as W3CWebSocket } from "websocket";
import { WS_EVENTS, WS_STATUS } from './constants';
import { WS_CONFIG } from './config';

class WebSocketService {
    constructor() {
        // Core properties
        this.ws = null;
        this.isConnecting = false;
        this.config = null;
        
        // Event handlers storage
        this.messageHandlers = new Map();
        this.connectionHandlers = new Set();

        // Reconnection properties
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 3000; // Start with 3 seconds
        this.maxReconnectDelay = 30000; // Max 30 seconds
        this.reconnectTimeout = null;
        this.isIntentionalDisconnect = false;
    }

    connect(config = {}) {
        // Prevent multiple connections
        if (this.ws?.readyState === W3CWebSocket.OPEN || this.isConnecting) {
            return;
        }

        this.config = config;
        this.isConnecting = true;
        this.isIntentionalDisconnect = false;

        try {
            // Create WebSocket connection
            this.ws = new W3CWebSocket(
                config.url || WS_CONFIG.URL,
                WS_CONFIG.PROTOCOL
            );
            
            // Set up event handlers
            this.ws.onopen = () => {
                this.isConnecting = false;
                this.reconnectAttempts = 0; // Reset reconnect attempts
                this.notifyConnectionStatus(WS_STATUS.CONNECTED);
                this.authenticate();
            };

            this.ws.onclose = () => {
                this.isConnecting = false;
                this.notifyConnectionStatus(WS_STATUS.DISCONNECTED);
                if (!this.isIntentionalDisconnect) {
                    this.handleReconnect();
                }
            };

            this.ws.onerror = (error) => {
                // console.error removed
                this.isConnecting = false;
                this.notifyConnectionStatus(WS_STATUS.ERROR);
                if (!this.isIntentionalDisconnect) {
                    this.handleReconnect();
                }
            };

            this.ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    const handler = this.messageHandlers.get(message.receivedCommand);
                    if (handler) handler(message);
                } catch (error) {
                    // console.error removed
                }
            };

        } catch (error) {
            // console.error removed
            this.isConnecting = false;
            if (!this.isIntentionalDisconnect) {
                this.handleReconnect();
            }
        }
    }

    handleReconnect() {
        // Don't reconnect if max attempts reached or intentionally disconnected
        if (this.reconnectAttempts >= this.maxReconnectAttempts || this.isIntentionalDisconnect) {
            return;
        }

        // Clear any existing reconnect timeout
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }

        // Calculate exponential backoff delay
        const delay = Math.min(
            this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
            this.maxReconnectDelay
        );


        this.reconnectTimeout = setTimeout(() => {
            this.reconnectAttempts++;
            this.connect(this.config);
        }, delay);
    }

    authenticate() {
        const { orgId, session } = this.config;
        this.send({
            command: WS_EVENTS.AUTHENTICATE,
            requestData: {
                authToken: `Bearer ${session}`,
                organisationId: orgId
            }
        });
    }

    send(message) {
        if (this.ws?.readyState !== W3CWebSocket.OPEN) {
            // console.error removed
            return false;
        }

        try {
            this.ws.send(JSON.stringify(message));
            return true;
        } catch (error) {
            // console.error removed
            return false;
        }
    }

    getMessages(params) {
        return this.send({
            command: WS_EVENTS.GET_MESSAGES,
            requestData: {
                ...params
            }
        });
    }

    getUpdateChat(params) {
        return this.send({
            command: WS_EVENTS.GET_UPDATES,
            requestData: {
                ...params
            }
        });
    }

    sendChatMessage(messageData) {
        return this.send({
            command: WS_EVENTS.SEND_MESSAGE,
            data: messageData,
            requestData: {
                userId: this.config.orgId,
                organisationId: this.config.orgId
            }
        });
    }

    onMessage(command, handler) {
        this.messageHandlers.set(command, handler);
        return () => this.messageHandlers.delete(command);
    }

    onConnectionChange(handler) {
        this.connectionHandlers.add(handler);
        return () => this.connectionHandlers.delete(handler);
    }

    notifyConnectionStatus(status) {
        this.connectionHandlers.forEach(handler => handler(status));
    }

    disconnect() {
        // Mark as intentional disconnect
        this.isIntentionalDisconnect = true;

        // Clear reconnection timeout
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        
        // Reset reconnection attempts
        this.reconnectAttempts = 0;
        
        // Close connection if exists
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        
        // Clear handlers
        this.messageHandlers.clear();
        this.connectionHandlers.clear();
        this.isConnecting = false;
    }

    // Helper method to check connection status
    isConnected() {
        return this.ws?.readyState === W3CWebSocket.OPEN;
    }
}

export default new WebSocketService();